import React from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Stack,
  Container,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Tooltip as MuiTooltip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton
} from '@mui/material';
import { 
  PlusCircle, 
  List, 
  Users, 
  FileText,
  ArrowRight,
  TrendingUp,
  DollarSign,
  Briefcase,
  Target,
  Clock,
  Percent,
  CalendarClock,
  FolderKanban,
  CheckSquare,
  AlertCircle,
  Play,
  CheckCircle,
  ExternalLink,
  FileCode,
  Wrench
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  PieChart, 
  Pie, 
  Cell, 
  AreaChart, 
  Area 
} from 'recharts';

const COLORS = ['#00d4aa', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const statusLabels = {
  SUBMITTED: 'Soumis',
  ANALYSIS: 'Analyse',
  QUOTED: 'Devis',
  ACCEPTED: 'Validé',
  REJECTED: 'Refusé',
  DEVELOPMENT: 'Dév',
  COMPLETED: 'Terminé'
};

function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPERADMIN';
  const isDev = user?.role === 'DEV' || user?.role === 'TECHNICIEN';
  const isInternalRole = isAdmin || isDev;
  const [data, setData] = React.useState(null);
  const [devData, setDevData] = React.useState(null);
  const [loading, setLoading] = React.useState(isInternalRole);

  React.useEffect(() => {
    if (isInternalRole) {
      const fetchStats = async () => {
        try {
          const token = localStorage.getItem('portalToken');
          
          if (isDev && !isAdmin) {
            // DEV/TECHNICIEN: fetch specific stats
            const response = await fetch('/api/submissions/dev/stats', { 
              headers: { 'Authorization': `Bearer ${token}` } 
            });
            if (response.ok) {
              const result = await response.json();
              setDevData(result);
            } else {
              // Set empty data to show "no projects" state instead of infinite loading
              setDevData({
                kpis: { totalAssignedProjects: 0, activeProjects: 0, todoItems: 0, inProgressItems: 0, doneItems: 0, estimatedHours: 0, actualHours: 0, hoursCompletion: 0 },
                projectsList: [],
                pendingItems: [],
                recentDocs: [],
                statusDistribution: [],
                itemsDistribution: [
                  { name: 'À faire', value: 0, color: '#f59e0b' },
                  { name: 'En cours', value: 0, color: '#3b82f6' },
                  { name: 'Terminé', value: 0, color: '#10b981' }
                ]
              });
            }
          } else {
            // ADMIN/SUPERADMIN: fetch admin stats
            const response = await fetch('/api/submissions/admin/stats', { 
              headers: { 'Authorization': `Bearer ${token}` } 
            });
            const result = await response.json();
            if (response.ok) setData(result);
          }
        } catch (err) {
          console.error('Stats fetch error:', err);
          // Set empty data for DEV on error
          if (isDev && !isAdmin) {
            setDevData({
              kpis: { totalAssignedProjects: 0, activeProjects: 0, todoItems: 0, inProgressItems: 0, doneItems: 0, estimatedHours: 0, actualHours: 0, hoursCompletion: 0 },
              projectsList: [],
              pendingItems: [],
              recentDocs: [],
              statusDistribution: [],
              itemsDistribution: [
                { name: 'À faire', value: 0, color: '#f59e0b' },
                { name: 'En cours', value: 0, color: '#3b82f6' },
                { name: 'Terminé', value: 0, color: '#10b981' }
              ]
            });
          }
        }
        setLoading(false);
      };
      fetchStats();
    }
  }, [isInternalRole, isDev, isAdmin]);

  const clientActions = [
    {
      title: 'Nouvelle Soumission',
      desc: 'Créer une demande pour un nouveau projet',
      icon: <PlusCircle size={32} color="#00d4aa" />,
      path: isAdmin ? '/admin/submissions/new' : '/portal/submissions/new',
      color: '#00d4aa'
    },
    {
      title: 'Mes Soumissions',
      desc: 'Suivre l\'état de vos projets en cours',
      icon: <List size={32} color="#3b82f6" />,
      path: '/portal/submissions',
      color: '#3b82f6'
    }
  ];

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
      <CircularProgress color="primary" />
    </Box>
  );

  if (!isInternalRole) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ minHeight: '70vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', py: 4 }}>
          <Paper sx={{ p: 5, borderRadius: 4, bgcolor: 'background.paper', border: '1px solid rgba(255, 255, 255, 0.06)', width: '100%', maxWidth: 900 }}>
            <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1, letterSpacing: -1 }}>
              Bienvenue{(user.firstName || user.lastName) ? ', ' : ''}
              <span style={{ color: '#00d4aa' }}>{`${user.firstName || ''} ${user.lastName || ''}`.trim()}</span>
            </Typography>
            <Typography color="text.secondary" variant="h6" sx={{ mb: 6, opacity: 0.8 }}>
              Gérez vos projets et suivez l'évolution de vos demandes.
            </Typography>
            <Grid container spacing={4} justifyContent="center">
              {clientActions.map((action, idx) => (
                <Grid item xs={12} sm={6} key={idx}>
                  <Card onClick={() => navigate(action.path)} sx={{ height: '100%', cursor: 'pointer', transition: 'all 0.3s', bgcolor: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: 3, '&:hover': { transform: 'translateY(-8px)', borderColor: action.color, bgcolor: 'rgba(255, 255, 255, 0.05)' } }}>
                    <CardContent sx={{ p: 4, textAlign: 'left' }}>
                      <Box sx={{ mb: 3 }}>{action.icon}</Box>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>{action.title}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{action.desc}</Typography>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ color: action.color, fontWeight: 'bold' }}>
                        <span>Accéder</span><ArrowRight size={18} />
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Box>
      </Container>
    );
  }

  // DEV / TECHNICIEN View
  if (isDev && !isAdmin) {
    // If devData not loaded yet or failed, show loading/empty state
    if (!devData) {
      return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
              Dashboard {user?.role === 'DEV' ? 'Développeur' : 'Technicien'}
            </Typography>
            <Typography color="text.secondary">Chargement de vos données...</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress color="primary" />
          </Box>
        </Container>
      );
    }
    
    const roleLabel = user?.role === 'DEV' ? 'Développeur' : 'Technicien';
    const roleIcon = user?.role === 'DEV' ? <FileCode size={24} color="#8b5cf6" /> : <Wrench size={24} color="#f59e0b" />;
    const accentColor = user?.role === 'DEV' ? '#8b5cf6' : '#f59e0b';

    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            {roleIcon}
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                Dashboard {roleLabel}
              </Typography>
              <Typography color="text.secondary">
                Bienvenue, {user?.firstName || user?.email}. Voici vos projets et tâches assignés.
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* KPIs Row */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={6} sm={3}>
            <Card sx={{ bgcolor: `${accentColor}10`, border: `1px solid ${accentColor}40`, borderRadius: 3 }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="caption" sx={{ color: accentColor, fontWeight: 'bold' }}>PROJETS ASSIGNÉS</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>{devData.kpis.totalAssignedProjects}</Typography>
                  </Box>
                  <FolderKanban size={32} color={accentColor} style={{ opacity: 0.5 }} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ bgcolor: 'rgba(59, 130, 246, 0.05)', border: '1px solid rgba(59, 130, 246, 0.2)', borderRadius: 3 }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="caption" sx={{ color: '#3b82f6', fontWeight: 'bold' }}>PROJETS ACTIFS</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>{devData.kpis.activeProjects}</Typography>
                  </Box>
                  <Play size={32} color="#3b82f6" style={{ opacity: 0.5 }} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ bgcolor: 'rgba(245, 158, 11, 0.05)', border: '1px solid rgba(245, 158, 11, 0.2)', borderRadius: 3 }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="caption" sx={{ color: '#f59e0b', fontWeight: 'bold' }}>TÂCHES À FAIRE</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>{devData.kpis.todoItems}</Typography>
                  </Box>
                  <AlertCircle size={32} color="#f59e0b" style={{ opacity: 0.5 }} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ bgcolor: 'rgba(16, 185, 129, 0.05)', border: '1px solid rgba(16, 185, 129, 0.2)', borderRadius: 3 }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 'bold' }}>TÂCHES TERMINÉES</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>{devData.kpis.doneItems}</Typography>
                  </Box>
                  <CheckCircle size={32} color="#10b981" style={{ opacity: 0.5 }} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Hours Progress */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <Card sx={{ bgcolor: 'background.paper', borderRadius: 3, border: '1px solid rgba(255,255,255,0.06)', height: '100%' }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>Progression Heures</Typography>
                <Stack spacing={2}>
                  <Box>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">Heures estimées</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{devData.kpis.estimatedHours}h</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">Heures réelles</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#00d4aa' }}>{devData.kpis.actualHours}h</Typography>
                    </Stack>
                    <Box sx={{ mt: 2 }}>
                      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">Completion</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{devData.kpis.hoursCompletion}%</Typography>
                      </Stack>
                      <LinearProgress 
                        variant="determinate" 
                        value={Math.min(devData.kpis.hoursCompletion, 100)} 
                        sx={{ 
                          height: 8, 
                          borderRadius: 4,
                          bgcolor: 'rgba(255,255,255,0.1)',
                          '& .MuiLinearProgress-bar': { 
                            bgcolor: devData.kpis.hoursCompletion > 100 ? '#ef4444' : '#00d4aa',
                            borderRadius: 4 
                          }
                        }}
                      />
                    </Box>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Items Distribution */}
          <Grid item xs={12} md={4}>
            <Card sx={{ bgcolor: 'background.paper', borderRadius: 3, border: '1px solid rgba(255,255,255,0.06)', height: 250 }}>
              <CardContent sx={{ height: '100%' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>Distribution Tâches</Typography>
                <ResponsiveContainer width="100%" height="70%">
                  <PieChart>
                    <Pie
                      data={devData.itemsDistribution}
                      innerRadius={40}
                      outerRadius={60}
                      paddingAngle={5}
                      dataKey="value"
                      nameKey="name"
                    >
                      {devData.itemsDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#111118', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <Stack direction="row" justifyContent="center" spacing={1} flexWrap="wrap" useFlexGap>
                  {devData.itemsDistribution.map((entry, index) => (
                    <Chip 
                      key={index} 
                      label={`${entry.name}: ${entry.value}`} 
                      size="small" 
                      sx={{ bgcolor: 'transparent', border: `1px solid ${entry.color}`, color: entry.color, fontSize: '0.65rem' }} 
                    />
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Recent Docs */}
          <Grid item xs={12} md={4}>
            <Card sx={{ bgcolor: 'background.paper', borderRadius: 3, border: '1px solid rgba(255,255,255,0.06)', height: 250 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>Documents Récents</Typography>
                {devData.recentDocs.length > 0 ? (
                  <Stack spacing={1}>
                    {devData.recentDocs.map((doc) => (
                      <Box 
                        key={doc.id}
                        component={Link}
                        to={`/portal/submissions/${doc.projectId}/documents`}
                        sx={{ 
                          p: 1.5, 
                          bgcolor: 'rgba(255,255,255,0.02)', 
                          borderRadius: 1,
                          textDecoration: 'none',
                          color: 'inherit',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' }
                        }}
                      >
                        <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>{doc.name}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>{doc.projectTitle}</Typography>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                    Aucun document récent
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* My Projects Table */}
        <Card sx={{ bgcolor: 'background.paper', borderRadius: 3, border: '1px solid rgba(255,255,255,0.06)', mb: 4 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Mes Projets Assignés</Typography>
              <Button 
                variant="outlined" 
                size="small" 
                endIcon={<ArrowRight size={16} />}
                onClick={() => navigate('/portal/submissions')}
                sx={{ borderColor: accentColor, color: accentColor }}
              >
                Voir tous
              </Button>
            </Stack>
            {devData.projectsList.length > 0 ? (
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'rgba(0,0,0,0.2)' }}>
                    <TableCell>Projet</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Entreprise</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell align="center">Éléments</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {devData.projectsList.slice(0, 8).map((project) => (
                    <TableRow 
                      key={project.id} 
                      hover 
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' } }}
                      onClick={() => navigate(`/portal/submissions/${project.id}`)}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{project.title}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={project.projectType} 
                          size="small"
                          sx={{ 
                            bgcolor: `${project.projectTypeColor}20`, 
                            color: project.projectTypeColor,
                            fontSize: '0.7rem',
                            height: 22
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{project.company}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={statusLabels[project.status] || project.status} 
                          size="small"
                          sx={{ 
                            bgcolor: project.status === 'DEVELOPMENT' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(255,255,255,0.05)',
                            color: project.status === 'DEVELOPMENT' ? '#3b82f6' : 'text.secondary',
                            fontSize: '0.7rem',
                            height: 22
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">{project.itemsCount}</Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/portal/submissions/${project.id}`); }}>
                          <ExternalLink size={14} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">Aucun projet assigné</Typography>
                <Typography variant="caption" color="text.secondary">
                  Demandez à un administrateur de vous assigner à des projets.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Pending Items */}
        {devData.pendingItems.length > 0 && (
          <Card sx={{ bgcolor: 'background.paper', borderRadius: 3, border: '1px solid rgba(255,255,255,0.06)' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <CheckSquare size={20} color="#f59e0b" />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Mes Tâches en Attente</Typography>
              </Stack>
              <Stack spacing={1}>
                {devData.pendingItems.map((item) => (
                  <Paper
                    key={item.id}
                    component={Link}
                    to={`/portal/submissions/${item.projectId}`}
                    sx={{ 
                      p: 2, 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2,
                      bgcolor: 'rgba(255,255,255,0.02)',
                      border: '1px solid rgba(255,255,255,0.06)',
                      borderRadius: 1.5,
                      textDecoration: 'none',
                      color: 'inherit',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.04)', borderColor: `${accentColor}40` }
                    }}
                  >
                    <Box sx={{ 
                      p: 0.75, 
                      borderRadius: 1, 
                      bgcolor: item.status === 'IN_PROGRESS' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(245, 158, 11, 0.15)',
                      color: item.status === 'IN_PROGRESS' ? '#3b82f6' : '#f59e0b'
                    }}>
                      {item.status === 'IN_PROGRESS' ? <Play size={14} /> : <AlertCircle size={14} />}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{item.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.projectTitle} • {item.sectionName}
                      </Typography>
                    </Box>
                    <Chip 
                      label={item.status === 'IN_PROGRESS' ? 'En cours' : 'À faire'}
                      size="small"
                      sx={{ 
                        bgcolor: item.status === 'IN_PROGRESS' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(245, 158, 11, 0.15)',
                        color: item.status === 'IN_PROGRESS' ? '#3b82f6' : '#f59e0b',
                        fontSize: '0.65rem',
                        height: 20
                      }}
                    />
                    {item.hours > 0 && (
                      <Typography variant="caption" color="text.secondary">{item.hours}h</Typography>
                    )}
                  </Paper>
                ))}
              </Stack>
            </CardContent>
          </Card>
        )}

        <Box sx={{ mt: 6, opacity: 0.5, textAlign: 'center' }}>
          <Typography variant="caption" sx={{ letterSpacing: 1 }}>
            Global TI Devis et Gestion de Projets Applicatifs &copy; Globalti {new Date().getFullYear()}
          </Typography>
        </Box>
      </Container>
    );
  }

  // Admin / SuperAdmin View
  if (!data) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
      <Typography color="text.secondary">Chargement des données analytiques...</Typography>
    </Box>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>Dashboard Analytique</Typography>
        <Typography color="text.secondary">Vue d'ensemble de la performance et du pipeline GlobalTI.</Typography>
      </Box>

      {/* KPI Row 1 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'rgba(0, 212, 170, 0.05)', border: '1px solid rgba(0, 212, 170, 0.2)', borderRadius: 3 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold' }}>VALEUR DU PIPELINE</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                    {new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(data.kpis.totalPipeline)}
                  </Typography>
                </Box>
                <DollarSign size={32} color="#00d4aa" style={{ opacity: 0.5 }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'rgba(59, 130, 246, 0.05)', border: '1px solid rgba(59, 130, 246, 0.2)', borderRadius: 3 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" sx={{ color: '#3b82f6', fontWeight: 'bold' }}>PROJETS ACTIFS</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>{data.kpis.activeProjects}</Typography>
                </Box>
                <Briefcase size={32} color="#3b82f6" style={{ opacity: 0.5 }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'rgba(139, 92, 246, 0.05)', border: '1px solid rgba(139, 92, 246, 0.2)', borderRadius: 3 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" sx={{ color: '#8b5cf6', fontWeight: 'bold' }}>PANIER MOYEN</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                    {new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(data.kpis.avgProjectValue)}
                  </Typography>
                </Box>
                <Target size={32} color="#8b5cf6" style={{ opacity: 0.5 }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'rgba(245, 158, 11, 0.05)', border: '1px solid rgba(245, 158, 11, 0.2)', borderRadius: 3 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" sx={{ color: '#f59e0b', fontWeight: 'bold' }}>SOUMISSIONS</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>{data.kpis.totalSubmissions}</Typography>
                </Box>
                <TrendingUp size={32} color="#f59e0b" style={{ opacity: 0.5 }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* KPI Row 2 - Nouvelles métriques */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ bgcolor: 'rgba(236, 72, 153, 0.05)', border: '1px solid rgba(236, 72, 153, 0.2)', borderRadius: 3 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" sx={{ color: '#ec4899', fontWeight: 'bold' }}>HEURES ESTIMÉES</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                    {new Intl.NumberFormat('fr-CA').format(data.kpis.totalHours || 0)}h
                  </Typography>
                </Box>
                <Clock size={32} color="#ec4899" style={{ opacity: 0.5 }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ bgcolor: 'rgba(16, 185, 129, 0.05)', border: '1px solid rgba(16, 185, 129, 0.2)', borderRadius: 3 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 'bold' }}>TAUX CONVERSION</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                    {data.kpis.conversionRate || 0}%
                  </Typography>
                </Box>
                <Percent size={32} color="#10b981" style={{ opacity: 0.5 }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ bgcolor: 'rgba(99, 102, 241, 0.05)', border: '1px solid rgba(99, 102, 241, 0.2)', borderRadius: 3 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" sx={{ color: '#6366f1', fontWeight: 'bold' }}>RÉCURRENT MENSUEL</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                    {new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(data.kpis.monthlyRecurring || 0)}
                  </Typography>
                </Box>
                <CalendarClock size={32} color="#6366f1" style={{ opacity: 0.5 }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Charts */}
      <Grid container spacing={3}>
        {/* Monthly Volume */}
        <Grid item xs={12} md={8}>
          <Card sx={{ bgcolor: 'background.paper', borderRadius: 3, border: '1px solid rgba(255,255,255,0.06)', height: 400 }}>
            <CardContent sx={{ height: '100%' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>Volume des demandes (6 mois)</Typography>
              <ResponsiveContainer width="100%" height="80%">
                <AreaChart data={data.monthlyGrowth}>
                  <defs>
                    <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#00d4aa" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#00d4aa" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="name" stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#111118', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                    itemStyle={{ color: '#00d4aa' }}
                    labelStyle={{ color: '#94a3b8' }}
                  />
                  <Area type="monotone" dataKey="count" name="Demandes" stroke="#00d4aa" strokeWidth={3} fillOpacity={1} fill="url(#colorCount)" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Status Distribution */}
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: 'background.paper', borderRadius: 3, border: '1px solid rgba(255,255,255,0.06)', height: 400 }}>
            <CardContent sx={{ height: '100%' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>Répartition par statut</Typography>
              <ResponsiveContainer width="100%" height="80%">
                <PieChart>
                  <Pie
                    data={data.statusDistribution}
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    nameKey="name"
                  >
                    {data.statusDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name) => [value, statusLabels[name] || name]}
                    contentStyle={{ backgroundColor: '#111118', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                    itemStyle={{ color: '#fff' }}
                    labelStyle={{ color: '#94a3b8' }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <Stack direction="row" flexWrap="wrap" justifyContent="center" spacing={1} useFlexGap>
                {data.statusDistribution.map((entry, index) => (
                  <Chip 
                    key={index} 
                    label={`${statusLabels[entry.name] || entry.name}: ${entry.value}`} 
                    size="small" 
                    sx={{ bgcolor: 'transparent', border: `1px solid ${COLORS[index % COLORS.length]}`, color: COLORS[index % COLORS.length], fontSize: '0.65rem' }} 
                  />
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Pipeline Value by Status */}
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: 'background.paper', borderRadius: 3, border: '1px solid rgba(255,255,255,0.06)', height: 400 }}>
            <CardContent sx={{ height: '100%' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>Valeur Pipeline par Statut</Typography>
              <ResponsiveContainer width="100%" height="80%">
                <BarChart data={data.budgetPipeline} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="rgba(255,255,255,0.05)" />
                  <XAxis type="number" hide />
                  <YAxis 
                    dataKey="name" 
                    type="category" 
                    stroke="#64748b" 
                    fontSize={11} 
                    width={100}
                    tickFormatter={(val) => statusLabels[val] || val}
                  />
                  <Tooltip 
                    formatter={(value) => new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD' }).format(value)}
                    contentStyle={{ backgroundColor: '#111118', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                    itemStyle={{ color: '#fff' }}
                    labelStyle={{ color: '#94a3b8' }}
                  />
                  <Bar dataKey="value" name="Valeur" radius={[0, 4, 4, 0]}>
                    {data.budgetPipeline.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Clients */}
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: 'background.paper', borderRadius: 3, border: '1px solid rgba(255,255,255,0.06)', height: 400 }}>
            <CardContent sx={{ height: '100%' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>Top 5 Clients (Valeur projets)</Typography>
              <ResponsiveContainer width="100%" height="80%">
                <BarChart data={data.topClients}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="name" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} hide />
                  <Tooltip 
                    formatter={(value) => new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD' }).format(value)}
                    contentStyle={{ backgroundColor: '#111118', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                    itemStyle={{ color: '#fff' }}
                    labelStyle={{ color: '#94a3b8' }}
                  />
                  <Bar dataKey="value" name="CA Potentiel" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Distribution par Type de Projet */}
        {data.typeDistribution && data.typeDistribution.length > 0 && (
          <Grid item xs={12} md={4}>
            <Card sx={{ bgcolor: 'background.paper', borderRadius: 3, border: '1px solid rgba(255,255,255,0.06)', height: 400 }}>
              <CardContent sx={{ height: '100%' }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                  <FolderKanban size={20} color="#8b5cf6" />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Répartition par Type</Typography>
                </Stack>
                <ResponsiveContainer width="100%" height="65%">
                  <PieChart>
                    <Pie
                      data={data.typeDistribution}
                      innerRadius={50}
                      outerRadius={70}
                      paddingAngle={5}
                      dataKey="value"
                      nameKey="name"
                    >
                      {data.typeDistribution.map((entry, index) => (
                        <Cell key={`cell-type-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name) => [`${value} projet(s)`, name]}
                      contentStyle={{ backgroundColor: '#111118', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                      itemStyle={{ color: '#fff' }}
                      labelStyle={{ color: '#94a3b8' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <Stack direction="row" flexWrap="wrap" justifyContent="center" spacing={1} useFlexGap sx={{ mt: 1 }}>
                  {data.typeDistribution.map((entry, index) => (
                    <Chip 
                      key={index} 
                      label={`${entry.name}: ${entry.value}`} 
                      size="small" 
                      sx={{ 
                        bgcolor: 'transparent', 
                        border: `1px solid ${entry.color || COLORS[index % COLORS.length]}`, 
                        color: entry.color || COLORS[index % COLORS.length], 
                        fontSize: '0.65rem' 
                      }} 
                    />
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Valeur Pipeline par Type de Projet */}
        {data.valueByType && data.valueByType.length > 0 && (
          <Grid item xs={12} md={8}>
            <Card sx={{ bgcolor: 'background.paper', borderRadius: 3, border: '1px solid rgba(255,255,255,0.06)', height: 400 }}>
              <CardContent sx={{ height: '100%' }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                  <DollarSign size={20} color="#00d4aa" />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Valeur Pipeline par Type de Projet</Typography>
                </Stack>
                <ResponsiveContainer width="100%" height="80%">
                  <BarChart data={data.valueByType}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                    <XAxis 
                      dataKey="name" 
                      stroke="#64748b" 
                      fontSize={11} 
                      tickLine={false} 
                      axisLine={false}
                      interval={0}
                      angle={-20}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} hide />
                    <Tooltip 
                      formatter={(value) => new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD' }).format(value)}
                      contentStyle={{ backgroundColor: '#111118', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                      itemStyle={{ color: '#fff' }}
                      labelStyle={{ color: '#94a3b8' }}
                    />
                    <Bar dataKey="value" name="Valeur" radius={[4, 4, 0, 0]}>
                      {data.valueByType.map((entry, index) => (
                        <Cell key={`cell-value-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      <Box sx={{ mt: 6, opacity: 0.5, textAlign: 'center' }}>
        <Typography variant="caption" sx={{ letterSpacing: 1 }}>
          Global TI Devis et Gestion de Projets Applicatifs &copy; Globalti {new Date().getFullYear()}
        </Typography>
      </Box>
    </Container>
  );
}

export default Dashboard;
